import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { TextField, Button, Box, MenuItem, Select, InputLabel, FormControl, CircularProgress, Typography } from '@mui/material';
import ExplanationEditor from '../../components/editor/ExplanationEditor';

const PUBLISHERS = [
  'Uworld', 'Simulated', 'Barbri', 'PMBR', 'Kaplan'
];

const CHOICE_LABELS = [
  { label: 'A', value: 1 },
  { label: 'B', value: 2 },
  { label: 'C', value: 3 },
  { label: 'D', value: 4 },
];

// Quill 관련 코드 제거 - RichTextEditor에서 이미지 업로드 자체 처리

export default function QuestionForm({ editingQuestion }) {
  const [form, setForm] = useState({
    publisher: '',
    custompublisher: '',
    subject_id: '',
    customsubject: '',
    set_id: '',
    question_text: '',
    choice_1: '',
    choice_2: '',
    choice_3: '',
    choice_4: '',
    correct_choice: '',
    explanation: '',
    explanation_en: '',
    explanation_ko: '',
  });

  const [subjects, setSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubjects = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.from('subjects').select('id, name');
        if (error) throw error;
        setSubjects(data || []);
      } catch (error) {
        console.error("Failed to fetch subjects", error);
        alert('과목 목록을 불러오는 데 실패했습니다.');
      }
      setIsLoading(false);
    };
    fetchSubjects();
  }, []);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const [explanation, setExplanation] = useState('');
  const [explanationEn, setExplanationEn] = useState('');
  const [explanationKo, setExplanationKo] = useState('');

  useEffect(() => {
    if (editingQuestion) {
      setForm({
        publisher: editingQuestion.publisher || '',
        custompublisher: editingQuestion.custompublisher || '',
        subject_id: editingQuestion.subject_id || '',
        customsubject: editingQuestion.customsubject || '',
        set_id: editingQuestion.set_id || '',
        question_text: editingQuestion.question_text || '',
        choice_1: editingQuestion.choice_1 || '',
        choice_2: editingQuestion.choice_2 || '',
        choice_3: editingQuestion.choice_3 || '',
        choice_4: editingQuestion.choice_4 || '',
        correct_choice: editingQuestion.correct_choice || '',
      });
      setExplanation(editingQuestion.explanation || '');
      setExplanationEn(editingQuestion.explanation_en || '');
      setExplanationKo(editingQuestion.explanation_ko || '');
    } else {
      setForm({
        publisher: '', custompublisher: '', subject_id: '', customsubject: '',
        set_id: '', question_text: '',
        choice_1: '', choice_2: '', choice_3: '', choice_4: '',
        correct_choice: '',
      });
      setExplanation('');
      setExplanationEn('');
      setExplanationKo('');
    }
  }, [editingQuestion]);

  const handleSubmit = async e => {
    e.preventDefault();
    
    const { id, created_at, ...submissionData } = {
        ...form,
        set_id: form.set_id ? Number(form.set_id) : null,
        correct_choice: form.correct_choice ? Number(form.correct_choice) : null,
        explanation,
        explanation_en: explanationEn,
        explanation_ko: explanationKo,
    };
    
    try {
      if (editingQuestion) {
        const { data, error } = await supabase.from('mbe_questions')
          .update(submissionData)
          .eq('id', editingQuestion.id);
        if (error) throw error;
        alert('문제 수정 완료!');
      } else {
        const { data, error } = await supabase.from('mbe_questions').insert([submissionData]);
        if (error) throw error;
        alert('문제 저장 완료!');
        
        // 신규 문제 저장 후 폼 리셋 (연속 입력 지원)
        setForm({
          publisher: form.publisher, // 출판사는 유지
          custompublisher: form.custompublisher,
          subject_id: form.subject_id, // 과목은 유지  
          customsubject: form.customsubject,
          set_id: form.set_id, // 세트 ID도 유지
          question_text: '',
          choice_1: '', choice_2: '', choice_3: '', choice_4: '',
          correct_choice: '',
        });
        setExplanation('');
        setExplanationEn('');
        setExplanationKo('');
      }
    } catch (error) {
      console.error('저장 오류:', error);
      alert(`저장 실패: ${error.message || '알 수 없는 오류가 발생했습니다.'}`);
    }
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>과목 목록을 불러오는 중입니다...</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl>
        <InputLabel>출판사</InputLabel>
        <Select name="publisher" value={form.publisher} label="출판사" onChange={handleChange}>
          <MenuItem value="">(선택 안함)</MenuItem>
          {PUBLISHERS.map(pub => (
            <MenuItem key={pub} value={pub}>{pub}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <InputLabel>과목</InputLabel>
        <Select name="subject_id" value={form.subject_id} label="과목" onChange={handleChange} required>
          <MenuItem value="">(선택 안함)</MenuItem>
          {subjects.map(sub => (
            <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField name="set_id" label="세트 ID" value={form.set_id} onChange={handleChange} />
      <TextField name="question_text" label="문제 내용" value={form.question_text} onChange={handleChange} required multiline />
      <TextField name="choice_1" label="선택지 A" value={form.choice_1} onChange={handleChange} required />
      <TextField name="choice_2" label="선택지 B" value={form.choice_2} onChange={handleChange} required />
      <TextField name="choice_3" label="선택지 C" value={form.choice_3} onChange={handleChange} required />
      <TextField name="choice_4" label="선택지 D" value={form.choice_4} onChange={handleChange} required />
      
      <FormControl required>
        <InputLabel>정답</InputLabel>
        <Select name="correct_choice" value={form.correct_choice} label="정답" onChange={handleChange}>
          {CHOICE_LABELS.map(choice => (
            <MenuItem key={choice.label} value={choice.value}>{choice.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <div>
        <div>해설 (원본, 해설지 복사)</div>
        <ExplanationEditor value={explanation} onChange={setExplanation} />
      </div>
      <div>
        <div>추가 영문 해설 (내가 직접 정리)</div>
        <ExplanationEditor value={explanationEn} onChange={setExplanationEn} />
      </div>
      <div>
        <div>한국어 해설 (내가 직접 정리)</div>
        <ExplanationEditor value={explanationKo} onChange={setExplanationKo} />
      </div>
      
      <Button type="submit" variant="contained" color="primary">저장</Button>
    </Box>
  );
}
